import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
  SignInProviderConfig,
} from '@backstage/core-components';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { ApiExplorerPage, apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { HomepageCompositionRoot, VisitListener } from '@backstage/plugin-home';
import { orgPlugin } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  TechDocsReaderPage,
  techdocsPlugin,
} from '@backstage/plugin-techdocs';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { UnifiedThemeProvider } from '@backstage/theme';
import { RbacPage } from '@backstage-community/plugin-rbac';
import {
  CustomMetricsInsightsPage,
  customMetricsInsightsReadPermission,
} from '@internal/backstage-plugin-custom-metrics';
import {
  FeedbackDialog,
  ViewFeedbackPage,
} from '@internal/backstage-plugin-feedback';
import { npsReadPermission } from '@internal/backstage-plugin-feedback-common';
import { NpsPage } from '@internal/backstage-plugin-nps';
import WbSunnyIcon from '@material-ui/icons/WbSunny';
import { Route } from 'react-router-dom';

import { apis } from './apis';
import { Root } from './components/Root';
import { entityPage } from './components/catalog/entityPage';
import { HomePage } from './components/homePage';
import {
  AlphabeticalTemplateListPage,
  BitbucketProjectPickerFieldExtension,
  CloudAccountPickerFieldExtension,
  DynamicRepositoryNameFieldExtension,
  GitHubProjectPickerFieldExtension,
  ProductAreaPickerFieldExtension,
  QualityGatePickerFieldExtension,
  SystemPickerForGroupExtension,
  VcsRepositoryNameFieldExtension,
} from './components/scaffolder';
import { scaffolderFormProps } from './components/scaffolder/scaffolderFormProps';
import { searchPage } from './components/search/SearchPage';
import { carrierDarkTheme } from './theme/carrier-dark-theme';
import { carrierDefaultLightTheme } from './theme/carrier-default-theme';

const entraProvider: SignInProviderConfig = {
  apiRef: microsoftAuthApiRef,
  id: 'microsoft',
  message: 'Sign in using Microsoft Entra',
  title: 'Microsoft Entra',
};

const DarkIcon = WbSunnyIcon;
const LightIcon = WbSunnyIcon;

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      if (process.env.NODE_ENV === 'production') {
        return <SignInPage {...props} auto provider={entraProvider} />;
      }
      return (
        <SignInPage {...props} auto providers={[entraProvider, 'guest']} />
      );
    },
  },
  themes: [
    {
      id: 'carrier-default-light',
      title: 'Carrier-Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={carrierDefaultLightTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'carrier-default-dark',
      title: 'Carrier-Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={carrierDarkTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route
      path="/catalog"
      element={<CatalogIndexPage pagination={{ mode: 'offset', limit: 20 }} />}
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          formProps={scaffolderFormProps}
          components={{
            EXPERIMENTAL_TemplateListPageComponent: AlphabeticalTemplateListPage,
          }}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <BitbucketProjectPickerFieldExtension />
        <CloudAccountPickerFieldExtension />
        <DynamicRepositoryNameFieldExtension />
        <GitHubProjectPickerFieldExtension />
        <ProductAreaPickerFieldExtension />
        <QualityGatePickerFieldExtension />
        <SystemPickerForGroupExtension />
        <VcsRepositoryNameFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/rbac" element={<RbacPage />} />
    <Route
      path="/insights"
      element={
        <RequirePermission permission={customMetricsInsightsReadPermission}>
          <CustomMetricsInsightsPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/feedback" element={<ViewFeedbackPage />} />
    <Route
      path="/nps"
      element={
        <RequirePermission permission={npsReadPermission}>
          <NpsPage />
        </RequirePermission>
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>
        <VisitListener />
        {routes}
        <FeedbackDialog /> {/* Moved inside AppRouter so useLocation() works */}
      </Root>
    </AppRouter>
  </>,
);
// vim:et:sw=2:ts=2
